import { useState } from "react";

import Auth from "@aws-amplify/auth";
import axios from "axios";

import {
  Button,
  Input,
  Select,
  toastError,
  toastSuccess,
} from "@alpacahq/ui/old";

import { useLocalStorage } from "~/v2/hooks/useLocalStorage";

type GrantType =
  | "client_credentials"
  | "urn:ietf:params:oauth:grant-type:jwt-bearer"
  | "refresh_token";

const GRANT_TYPES: { label: string; value: GrantType }[] = [
  { label: "client_credentials", value: "client_credentials" },
  {
    label: "jwt-bearer (Cognito/Keycloak/Google)",
    value: "urn:ietf:params:oauth:grant-type:jwt-bearer",
  },
  { label: "refresh_token", value: "refresh_token" },
];

const AUTHX_BASE_URL = window?.env?.AUTHX_API_URL;
const AUTHX_TOKEN_URL = AUTHX_BASE_URL
  ? `${AUTHX_BASE_URL}/oauth2/token`
  : undefined;

export const AuthxTesting = () => {
  const [open, setOpen] = useLocalStorage<boolean>(
    "devtools-authx-testing-open",
  );

  const [grantType, setGrantType] = useState<GrantType>("client_credentials");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [clientAssertionType, setClientAssertionType] = useState("");
  const [clientAssertion, setClientAssertion] = useState("");
  const [assertion, setAssertion] = useState("");
  const [scope, setScope] = useState("");
  const [refreshToken, setRefreshToken] = useState("");
  const [response, setResponse] = useState("<response>");
  const [loading, setLoading] = useState(false);

  const applyCognitoExchangePreset = async () => {
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      setGrantType("urn:ietf:params:oauth:grant-type:jwt-bearer");
      setAssertion(token);
      setScope("party");
      setClientId("");
      setClientSecret("");
      setClientAssertionType("");
      setClientAssertion("");
      setRefreshToken("");
      setResponse("<response>");
      toastSuccess("Preset applied with current Cognito token", {});
    } catch {
      toastError("Failed to get Cognito session - are you logged in?", {});
    }
  };

  const handleSubmit = async () => {
    if (!AUTHX_TOKEN_URL) {
      toastError("AUTHX_API_URL is not configured", {});
      setResponse("AUTHX_API_URL is not configured");
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("grant_type", grantType);

      if (clientId) {
        params.append("client_id", clientId);
      }
      if (clientSecret) {
        params.append("client_secret", clientSecret);
      }
      if (clientAssertionType) {
        params.append("client_assertion_type", clientAssertionType);
      }
      if (clientAssertion) {
        params.append("client_assertion", clientAssertion);
      }
      if (assertion) {
        params.append("assertion", assertion);
      }
      if (scope) {
        params.append("scope", scope);
      }
      if (refreshToken) {
        params.append("refresh_token", refreshToken);
      }

      const res = await axios.post(AUTHX_TOKEN_URL, params, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        validateStatus: () => true,
      });

      if (res.status >= 200 && res.status < 300) {
        toastSuccess(`Token issued (${res.status})`, {});
      } else {
        toastError(`Request failed (${res.status})`, {});
      }
      setResponse(JSON.stringify(res.data, null, 2));
    } catch (error) {
      setResponse(JSON.stringify(error, null, 2));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setGrantType("client_credentials");
    setClientId("");
    setClientSecret("");
    setClientAssertionType("");
    setClientAssertion("");
    setAssertion("");
    setScope("");
    setRefreshToken("");
    setResponse("<response>");
  };

  return (
    <div className="overflow-hidden rounded border border-gray-300">
      <button
        type="button"
        onClick={() => setOpen(!(open ?? false))}
        className="flex w-full items-center justify-between bg-gray-50 px-4 py-3 transition-colors hover:bg-gray-100"
      >
        <h2 className="m-0 font-semibold text-lg">AuthX - OAuth2 Token</h2>
        <span className="text-gray-500">{(open ?? false) ? "−" : "+"}</span>
      </button>
      {(open ?? false) && (
        <div className="flex flex-col gap-4 p-4">
          {!AUTHX_TOKEN_URL && (
            <div className="rounded border border-red-200 bg-red-50 p-3 text-red-700 text-sm">
              AUTHX_API_URL is not configured. Set it before requesting AuthX
              tokens.
            </div>
          )}
          <div className="flex flex-row items-center gap-2">
            <Button onClick={reset}>Reset</Button>
            <Button onClick={applyCognitoExchangePreset}>
              Preset: Cognito Token Exchange
            </Button>
            <small className="text-gray-500">
              POST {AUTHX_TOKEN_URL ?? "AUTHX_API_URL not configured"}
            </small>
          </div>

          {/* Grant Type */}
          <div className="flex flex-col gap-1">
            <label className="font-medium text-gray-600 text-sm">
              grant_type <span className="text-red-500">*</span>
            </label>
            <Select
              options={GRANT_TYPES}
              value={grantType}
              onChange={(v) => setGrantType(v as GrantType)}
            />
          </div>

          {/* Client credentials fields */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className="font-medium text-gray-600 text-sm">
                client_id
              </label>
              <Input
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                placeholder="20-32 char alphanumeric"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="font-medium text-gray-600 text-sm">
                client_secret
              </label>
              <Input
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                placeholder="Client secret"
                type="password"
              />
            </div>
          </div>

          {/* Client assertion fields */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className="font-medium text-gray-600 text-sm">
                client_assertion_type
              </label>
              <Input
                value={clientAssertionType}
                onChange={(e) => setClientAssertionType(e.target.value)}
                placeholder="urn:ietf:params:oauth:client-assertion-type:jwt-bearer"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="font-medium text-gray-600 text-sm">
                client_assertion
              </label>
              <textarea
                value={clientAssertion}
                onChange={(e) => setClientAssertion(e.target.value)}
                placeholder="Client assertion JWT"
                className="h-16 w-full rounded border border-gray-300 px-2 py-1 text-sm"
              />
            </div>
          </div>

          {/* JWT bearer assertion */}
          {grantType === "urn:ietf:params:oauth:grant-type:jwt-bearer" && (
            <div className="flex flex-col gap-1">
              <label className="font-medium text-gray-600 text-sm">
                assertion
              </label>
              <textarea
                value={assertion}
                onChange={(e) => setAssertion(e.target.value)}
                placeholder="Assertion JWT (Cognito/Keycloak/Google token)"
                className="h-20 w-full rounded border border-gray-300 px-2 py-1 text-sm"
              />
            </div>
          )}

          {/* Scope */}
          <div className="flex flex-col gap-1">
            <label className="font-medium text-gray-600 text-sm">scope</label>
            <Input
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              placeholder="e.g. correspondent:ABCD or admin"
            />
          </div>

          {/* Refresh token */}
          {grantType === "refresh_token" && (
            <div className="flex flex-col gap-1">
              <label className="font-medium text-gray-600 text-sm">
                refresh_token <span className="text-red-500">*</span>
              </label>
              <textarea
                value={refreshToken}
                onChange={(e) => setRefreshToken(e.target.value)}
                placeholder="Refresh token"
                className="h-20 w-full rounded border border-gray-300 px-2 py-1 text-sm"
              />
            </div>
          )}

          <Button onClick={handleSubmit} disabled={loading || !AUTHX_TOKEN_URL}>
            {loading ? "Requesting..." : "Issue Token"}
          </Button>

          {/* Response */}
          <div>
            <h3 className="mb-2 font-semibold">Response</h3>
            <pre className="max-h-96 overflow-auto rounded bg-gray-50 p-2 text-sm">
              {response}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};
